import React, { useEffect, useState } from "react";
import { api, friendlyError } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Pill } from "../lib/ui";
import { Plus, X, Bell, Mail, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

const IMPACTS = ["Critico", "Alto", "Medio", "Basso", "Informativo"];
const FREQ = [{ v: "immediate", l: "Immediato" }, { v: "daily", l: "Digest giornaliero" }, { v: "weekly", l: "Digest settimanale" }];

export default function Watchlist() {
  const [wls, setWls] = useState([]);
  const [alerts, setAlerts] = useState({ frequency: "daily", email_digest: false, in_app: true, min_impact: "Alto" });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", keywords: "" });
  const [sending, setSending] = useState(false);

  const load = () => Promise.all([api.get("/watchlists"), api.get("/alerts")]).then(([w, a]) => { setWls(w.data); setAlerts(a.data); });
  useEffect(() => { load(); }, []);

  const createWl = async () => {
    try {
      await api.post("/watchlists", { name: form.name, keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Watchlist creata"); setShow(false); setForm({ name: "", keywords: "" }); load();
    } catch (e) { toast.error(friendlyError(e)); }
  };
  const delWl = async (id) => { await api.delete(`/watchlists/${id}`); load(); };

  const saveAlerts = async (patch) => {
    const next = { ...alerts, ...patch };
    setAlerts(next);
    try { await api.post("/alerts", { frequency: next.frequency, email_digest: next.email_digest, in_app: next.in_app, min_impact: next.min_impact }); }
    catch (e) { toast.error(friendlyError(e)); }
  };

  const testEmail = async () => {
    setSending(true);
    try { const { data } = await api.post("/alerts/test"); toast.success(`Email digest inviata (${data.items} aggiornamenti)`); }
    catch (e) { toast.error(friendlyError(e)); } finally { setSending(false); }
  };

  return (
    <div className="ri-fade-in max-w-4xl">
      <PageHeader overline="Watchlist & Smart Alert" title="Watchlist"
        subtitle="Watchlist personalizzate e alert prioritizzati sul profilo del tuo sito, tecnologie, prodotti e clienti."
        actions={<button onClick={() => setShow(true)} data-testid="wl-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-700 text-white text-sm hover:bg-blue-800"><Plus size={15} /> Nuova watchlist</button>} />

      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-3">
          {wls.map((w) => (
            <div key={w.id} data-testid={`wl-${w.id}`} className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium">{w.name}</div>
                <button onClick={() => delWl(w.id)} className="text-slate-300 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">{(w.keywords || []).map((k) => <Pill key={k} color="#1E40AF" bg="#DBEAFE">{k}</Pill>)}</div>
            </div>
          ))}
          {wls.length === 0 && <div className="text-sm text-slate-400 rounded-xl border border-dashed border-slate-200 p-6 text-center">Nessuna watchlist. Creane una per filtrare gli alert.</div>}
        </div>

        <div className="rounded-xl bg-white border border-slate-200 p-5 h-fit">
          <h3 className="font-head font-semibold text-sm mb-4 flex items-center gap-2"><Bell size={16} className="text-blue-600" /> Impostazioni alert</h3>
          <div className="space-y-4">
            <div>
              <div className="ri-overline mb-1.5">Frequenza</div>
              <div className="flex flex-wrap gap-1.5">
                {FREQ.map((f) => (
                  <button key={f.v} onClick={() => saveAlerts({ frequency: f.v })} data-testid={`freq-${f.v}`}
                    className={`px-2.5 py-1 rounded-md text-xs border ${alerts.frequency === f.v ? "bg-blue-600 text-white border-blue-600" : "border-slate-200"}`}>{f.l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="ri-overline mb-1.5">Severità minima</div>
              <select value={alerts.min_impact} onChange={(e) => saveAlerts({ min_impact: e.target.value })} data-testid="alert-min-impact" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
                {IMPACTS.map((i) => <option key={i}>{i}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={alerts.in_app} onChange={(e) => saveAlerts({ in_app: e.target.checked })} /> Notifiche in-app</label>
            <label className="flex items-center gap-2 text-sm" data-testid="alert-email-toggle"><input type="checkbox" checked={alerts.email_digest} onChange={(e) => saveAlerts({ email_digest: e.target.checked })} /> Digest via email</label>
            <button onClick={testEmail} disabled={sending} data-testid="alert-test-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-60">
              <Send size={15} /> {sending ? "Invio..." : "Invia digest di prova alla mia email"}
            </button>
            <p className="text-xs text-slate-400 flex items-start gap-1.5"><Mail size={12} className="mt-0.5" /> Il digest include gli aggiornamenti ad alta rilevanza per il tuo sito e le tue watchlist.</p>
          </div>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.6)" }}>
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-head font-semibold text-lg">Nuova watchlist</h3><button onClick={() => setShow(false)}><X size={18} /></button></div>
            <div className="space-y-3">
              <input placeholder="Nome (es. Sterile Manufacturing)" data-testid="wl-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <input placeholder="Keywords separate da virgola (es. Annex 1, isolator, CCS, APS)" data-testid="wl-keywords-input" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <button onClick={createWl} data-testid="wl-save-btn" className="w-full py-2.5 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800">Salva watchlist</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
